import { useEffect, useMemo, useRef, useState } from 'react';
import { useSuggestions } from 'utils/hooks';

import {
  Box,
  Button,
  Flex,
  // IconButton,
  ListBox,
  ListBoxItem,
  Popover,
  reactIcons as Icons,
  Spinner,
  Tag,
  TextField,
  Typography,
  useOverlayPosition,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

// TODO fix Element doesn't have click
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const overrideClick = (element: any) => {
  if (element.type === 'button' && element.click) element.click();
  return true;
};

interface AutocompleteProps {
  isDisabled?: boolean;
  multiple?: boolean;
  onChange?: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  suggestKey: string;
  suggestValue: string;
  value: string[];
}

function Autocomplete({
  isDisabled,
  multiple = false,
  onChange,
  placeholder,
  searchPlaceholder,
  suggestKey,
  suggestValue,
  value: inputValue,
}: AutocompleteProps) {
  const { t } = useTranslation();
  const { isError, isLoading, setQuery, suggestions, query } = useSuggestions(
    suggestKey,
    suggestValue,
  );

  const didMountRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selections, setSelections] = useState(inputValue.filter(Boolean));
  const [cache, setCache] = useState([...selections]);

  const overlayRef = useRef(null);
  const targetRef = useRef(null);
  const { overlayProps } = useOverlayPosition({
    isOpen,
    offset: 4,
    onClose: () => setIsOpen(false),
    placement: 'bottom left',
    overlayRef,
    targetRef,
  });

  useEffect(() => {
    if (!isOpen) setSearchText('');
    if (!isOpen) setCache(selections);
  }, [isOpen, selections]);

  useEffect(() => {
    setQuery(searchText);
  }, [searchText, setQuery]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
    } else {
      onChange && onChange(['']);
      setSelections([]);
    }
    // TODO FIX
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestKey]);

  const trigger = useMemo(
    () => (
      <Box
        css={{
          borderRadius: '$small',
          minHeight: 40,
          padding: '7px 12px',
          position: 'relative',
        }}
      >
        <Button
          ref={targetRef}
          onPress={() => setIsOpen(!isOpen)}
          data-testid="autocomplete"
          aria-label={t('common.openSuggestionsList')}
          variant="tertiary"
          isDisabled={isDisabled}
          css={{
            border: '1px solid $palette-grey-500',
            cursor: 'default',
            height: '100%',
            justifyContent: 'flex-end',
            left: 0,
            position: 'absolute',
            top: 0,
            width: '100%',
            '&:hover': { backgroundColor: 'transparent' },
            '&:focus': { borderColor: '$primary-active' },
          }}
          endIcon={
            <Icons.ChevronDown
              css={{ color: isOpen ? '$text-primary' : '$palette-grey-500' }}
            />
          }
        />
        <Flex
          data-testid="tags"
          wrap
          gap="x-small"
          align="center"
          css={{ minHeight: 26 }}
        >
          {!selections.length && (
            <Typography variant="subtext" children={placeholder} />
          )}
          {!!selections.length && !multiple && (
            <Typography variant="subtext" children={selections[0]} />
          )}
          {!!selections.length &&
            multiple &&
            selections.map((item) => (
              <Tag
                children={item}
                isRemovable={multiple}
                key={item}
                label={item}
                onRemove={() => {
                  setSelections((prev) => prev.filter((val) => val !== item));
                  const next = selections.filter((val) => val !== item);
                  onChange && onChange(next.length ? next : ['']);
                }}
              />
            ))}
        </Flex>
      </Box>
    ),
    [isDisabled, isOpen, multiple, onChange, placeholder, selections, t],
  );

  const content = useMemo(() => {
    const regex = new RegExp(query, 'i');
    const items = [...new Set([...suggestions, ...selections])]
      .filter((val) => !query || val.match(regex))
      .sort((a, b) => {
        if (cache.includes(a) && cache.includes(b)) return 0;
        return cache.includes(a) ? -1 : 1;
      })
      .map((item) => (
        <ListBoxItem key={item} css={{ width: '100%' }}>
          {item}
        </ListBoxItem>
      ));

    // TODO fix checked: Selection
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleSelection = (checked: any) => {
      const next = [...checked].sort() as string[];
      setSelections(next);
      if (onChange) onChange(next.length ? next : ['']);
      if (!multiple) setIsOpen(false);
    };

    return (
      <Popover
        ref={overlayRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        shouldCloseOnBlur
        shouldCloseOnInteractOutside={overrideClick}
        {...overlayProps}
      >
        <TextField
          css={{ padding: '$small $small 0' }}
          aria-label={t('common.searchSuggestions')}
          onChange={setSearchText}
          placeholder={searchPlaceholder}
          startIcon={<Icons.Search />}
          value={searchText}
          // TODO endIcon does not allow click
          // endIcon={
          //   searchText && (
          //     <IconButton
          //       size={'x-small' as any}
          //       variant="tertiary"
          //       onPress={() => setSearchText('')}
          //       children={<Icons.Close />}
          //     />
          //   )
          // }
        />
        <Box css={{ height: 252, overflowY: 'auto' }}>
          {isLoading && (
            <Flex align="center" justify="center" css={{ height: '100%' }}>
              <Spinner size="medium" />
            </Flex>
          )}
          {!isLoading && !items.length && (
            <Flex align="center" justify="center" css={{ height: 64 }}>
              <Typography>
                {isError ? t('common.errorOptions') : t('common.noOptions')}
              </Typography>
            </Flex>
          )}
          {!isLoading && !!items.length && (
            <ListBox
              aria-label={t('common.suggestionsList')}
              css={{ width: '100%' }}
              selectedKeys={selections}
              selectionMode={multiple ? 'multiple' : 'single'}
              children={items}
              onSelectionChange={handleSelection}
            />
          )}
        </Box>
      </Popover>
    );
  }, [
    cache,
    isError,
    isLoading,
    isOpen,
    multiple,
    onChange,
    overlayProps,
    searchPlaceholder,
    searchText,
    selections,
    suggestions,
    query,
    t,
  ]);

  return (
    <Box css={{ width: '100%' }}>
      {trigger}
      {isOpen && content}
    </Box>
  );
}

export default Autocomplete;
